<script lang="ts" setup>
import { SegmentationHorizontalCondition } from '#components'

const props = defineProps({
  companies: {
    default: undefined,
    type: Array as PropType<CompanyV2[]>,
  },
  feature: {
    default: 'model',
    type: String as PropType<SegmentationFeature>,
  },
  hasSortReset: {
    default: false,
    type: Boolean,
  },
  list: {
    default: undefined,
    type: Object as PropType<List>,
  },
  queryCount: {
    default: undefined,
    type: Number,
  },
})

const { checkAvailableCredits } = useSubscription()
const { checkIfConditionsAreSimilar, checkIfQueryNotEmpty, createCondition, getLabelForAttribute, getQueryFromUrl, synchronizeUrl } = useSegmentation()
const { sortFields } = useSegmentationSort()
const { t } = useI18n()
const guards = useGuards()
const route = useRoute()
const router = useRouter()
const { slideovers, toggleSlideover } = useOverlays()

const model = defineModel<{ condition: SegmentationCondition, domains: string[], query: SegmentationCondition[] }>({ required: false })

const segmentationHorizontalConditionRef = ref<InstanceType<typeof SegmentationHorizontalCondition>>()

const form = reactive({
  condition: createCondition({ query: getQueryFromUrl() }),
  domains: [] as string[],
  query: [] as SegmentationCondition[],
})

const badgesForDomains = computed((): UBadge[] => {
  return form.domains.map((domain): UBadge => {
    return {
      avatar: getLogo(domain, 'companies'),
      label: domain,
      value: domain,
    }
  })
})

const isConditionCreatorExpanded = computed(() => {
  return !form.query.length && ['enrichment', 'search'].includes(props.feature)
})

function isConditionEmpty(condition: SegmentationCondition) {
  if (!condition) {
    return true
  }

  return !checkIfQueryNotEmpty([condition])
}

const isQueryEmpty = computed(() => {
  return !checkIfQueryNotEmpty(form.query)
})

watch(
  () => form.domains,
  () => synchronizeUrl({
    domains: form.domains,
    query: form.query,
  }),
  { deep: true },
)

watch(
  () => form.query,
  () => synchronizeUrl({
    domains: form.domains,
    query: form.query,
  }),
  { deep: true },
)

watch(
  () => form,
  (newVal) => {
    model.value = newVal
  },
  {
    deep: true,
    immediate: true,
  },
)

watch(
  () => route.query,
  (_, oldQuery) => setForm(oldQuery),
  { immediate: true },
)

function getDomainsFromUrl() {
  const domains: string[] = []

  if (route.query.domains) {
    if (Array.isArray(route.query.domains)) {
      for (const domain of route.query.domains) {
        domains.push(`${domain}`)
      }
    }
    else {
      domains.push(route.query.domains)
    }
  }

  return domains
}

function resetForm(previousQuery?: LocationQuery) {
  let lastAttribute: SegmentationAttribute = 'about.industries'

  if (previousQuery) {
    const currentQuery = getQueryFromUrl(previousQuery)

    if (currentQuery.length) {
      lastAttribute = currentQuery[currentQuery.length - 1].attribute
    }
  }

  form.condition = createCondition({ attribute: lastAttribute })
  form.domains = []
  form.query = []
  model.value = form
}

function setForm(previousQuery?: LocationQuery) {
  // Adapt form to list
  if (props.list) {
    form.query = props.list.query as SegmentationCondition[] || []

    if (!form.query.length) {
      resetForm(previousQuery)
    }
  }
  // Generate form for search
  else {
    // Reset segmentation when going to root url "/companies"
    if (Object.keys(route?.query)?.length === 0) {
      resetForm(previousQuery)
    }
    // Or rebuild query from url
    else {
      const queryFromUrl = getQueryFromUrl()

      form.domains = getDomainsFromUrl()

      // Remove the first condition from queryFromUrl when it's form.condition
      if (checkIfConditionsAreSimilar(queryFromUrl[0], form.condition) || !queryFromUrl?.[0]?.values?.length) {
        form.query = queryFromUrl.slice(1)
      }
      else {
        form.query = queryFromUrl
      }
    }
  }
}

function onDomainSelected(domain: string) {
  if (!form.domains.includes(domain)) {
    form.domains.unshift(domain)
  }
}

function onRemoveConditionFromQuery(condition: SegmentationCondition) {
  const conditionIndex = form.query.findIndex(cond => checkIfConditionsAreSimilar(cond, condition))

  if (conditionIndex === 0 && form.query.length === 1) {
    resetForm()

    return
  }

  if (conditionIndex !== -1) {
    form.query = form.query.filter((_, index) => index !== conditionIndex)
  }
}

function onRemoveDomain(badge: UBadge) {
  form.domains = form.domains.filter((domain) => {
    return domain !== badge.value
  })
}

function onResetQuery() {
  resetForm()
}

function onResetSort() {
  router.push({
    name: route.name,
    params: { ...route?.params },
    query: {
      ...route?.query,
      sortFields: undefined,
    },
  })
}

async function onSaveCompanies() {
  try {
    if (guards.userOnly()) {
      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      })

      if (checkAvailableCredits()) {
        toggleSlideover({
          data: {
            companies: props.companies,
            queryCount: props.queryCount,
          },
          opened: true,
          type: 'listSaver',
        })
      }
    }
  }
  catch (error) {
    console.error(error)
  }
}

function onConfirmConditionToQuery() {
  form.query = [form.condition, ...form.query]
  form.condition = createCondition({ query: form.query })

  if (segmentationHorizontalConditionRef.value) {
    segmentationHorizontalConditionRef.value.opened = false
  }
}
</script>

<template>
  <div class="flex items-center gap-2">
    <div
      v-if="
        (!isQueryEmpty && ['enrichment', 'search'].includes(feature))
          || (hasSortReset && Object.keys(sortFields).length)
          || (feature === 'similar')
      "
      class="flex items-center gap-2"
    >
      <template v-if="feature === 'search'">
        <SegmentationAction
          v-if="!isQueryEmpty && ['enrichment', 'search'].includes(feature)"
          :icon="getIcon('save')"
          icon-color="text-lime-500"
          :label="t('globals.saveCompanies')"
          minimize-on-mobile
          :tooltip="t('globals.saveCompanies')"
          @click="onSaveCompanies"
        />

        <SegmentationAction
          v-if="hasSortReset && Object.keys(sortFields).length"
          :icon="getIcon('sortReset')"
          icon-color="text-amber-500"
          :label="t('globals.resetSort')"
          minimize-on-mobile
          :tooltip="t('globals.resetSort')"
          @click="onResetSort"
        />
      </template>

      <template v-else-if="feature === 'similar'">
        <CompanyAutocomplete
          class="w-full lg:w-64"
          :domains-to-exclude="form.domains"
          @selected="onDomainSelected"
        />

        <SegmentationAction
          v-if="form.domains.length"
          :icon="getIcon('save')"
          icon-color="text-lime-500"
          :label="t('globals.saveCompanies')"
          minimize-on-mobile
          :tooltip="t('globals.saveCompanies')"
          @click="onSaveCompanies"
        />
      </template>
    </div>

    <BaseVerticalDivider
      v-if="feature !== 'model' && (form.query.length || badgesForDomains.length)"
      class="mx-1 h-9"
    />

    <div class="relative flex flex-1 items-center gap-2 overflow-x-auto">
      <template v-if="feature === 'search' || feature === 'model'">
        <SegmentationHorizontalCondition
          ref="segmentationHorizontalConditionRef"
          v-model="form.condition"
          class="flex-shrink-0"
          :companies="companies"
          @confirm="onConfirmConditionToQuery"
        >
          <SegmentationAction
            :icon="getIcon('add')"
            icon-color="text-indigo-500"
            :label="!form.query.length ? t('globals.addCondition') : undefined"
            :prevent="isConditionCreatorExpanded"
            :tooltip="form.query.length ? t('globals.addCondition') : undefined"
          />

          <template #actions="{ condition, confirmCondition }">
            <UButton
              v-if="!isConditionEmpty(condition)"
              block
              :icon="getIcon('check')"
              :label="t('globals.confirmCondition')"
              size="md"
              :ui="{ icon: { base: 'text-green-600 dark:text-green-400' } }"
              variant="subtle"
              @click="confirmCondition"
            />
          </template>
        </SegmentationHorizontalCondition>

        <template v-if="!isQueryEmpty">
          <SegmentationHorizontalCondition
            v-for="(condition, conditionIndex) of form.query"
            :key="JSON.stringify(condition)"
            v-model="form.query[conditionIndex]"
            :companies="companies"
            has-tooltip
          >
            <template #default="{ condition: conditionSlot }">
              <UButton
                class="flex-shrink-0 text-xs uppercase"
                size="md"
                variant="subtle"
              >
                <UIcon
                  class="-mr-0.5 size-4"
                  :name="getIconForAttribute(conditionSlot.attribute)"
                />

                {{ getLabelForAttribute(conditionSlot.attribute) }}

                <span
                  v-if="conditionSlot.attribute !== 'ai.search'"
                  class="-ml-1 opacity-80"
                >
                  ({{ conditionSlot.values.length }})
                </span>
              </UButton>
            </template>

            <template #actions="{ condition: conditionSlot, isDifferent, confirmCondition }">
              <div class="flex w-full items-center gap-2">
                <div class="w-full flex-1">
                  <UButton
                    block
                    class="ring-red-600 dark:ring-red-400"
                    :class="{ '-mt-1': conditionSlot.attribute === 'ai.search' }"
                    color="red"
                    :icon="getIcon('trash')"
                    :label="t('globals.removeCondition')"
                    size="md"
                    variant="subtle"
                    @click="onRemoveConditionFromQuery(condition)"
                  />
                </div>

                <div class="w-full flex-1">
                  <UButton
                    block
                    :disabled="isConditionEmpty(condition) || !isDifferent"
                    :icon="getIcon('check')"
                    :label="t('globals.confirmCondition')"
                    size="md"
                    :ui="{ icon: { base: 'text-green-600 dark:text-green-400' } }"
                    variant="subtle"
                    @click="confirmCondition"
                  />
                </div>
              </div>
            </template>
          </SegmentationHorizontalCondition>

          <UButton
            class="hidden lg:-ml-1 lg:flex"
            :icon="getIcon('reset')"
            :label="t('globals.reset')"
            :ui="{ base: '!px-2' }"
            variant="link"
            @click="onResetQuery"
          />
        </template>
      </template>

      <BaseBadges
        v-else-if="feature === 'similar'"
        :badges="badgesForDomains"
        clickable
        removable
        size="md"
        variant="subtle"
        :wrap="false"
        @badge-click="onRemoveDomain"
      />
    </div>

    <SlideoverListSaver
      v-if="slideovers.listSaver.opened"
      v-model="slideovers.listSaver.opened"
      :domains="form.domains"
      :feature="feature"
      :list="list"
      :query="form.query"
      @close="
        toggleSlideover({
          opened: false,
          type: 'listSaver',
        })
      "
    />
  </div>
</template>
